/*
  Kiwi Ears · launch surface (Rene 2026-08-31) — sections/ln-hero, ln-reserve.

  Two jobs, both small:

  1. THE COUNTDOWN. Ticks once a second toward the target the section rendered.
     If the target is missing, unparseable, or already past, the dials are
     replaced by the section's own "Coming soon" line — a clock frozen at
     00:00:00 reads as a broken page, not as a launch. The dials start at "--"
     in the markup so there is never a flash of a wrong number before the first
     tick, and the whole block is aria-live="off": a screen reader does not need
     the seconds read to it every second, so the accessible answer is the static
     launch date, which the label carries.

  2. THE RESERVATION ADD-TO-CART. Same /cart/add.js pattern as the rest of the
     theme (kiwi-guide, kiwi-wishlist): post the variant, refresh the header
     count, open the shared drawer. The button is disabled while in flight so a
     double tap cannot buy two reservations.

  Run-once guard because two sections load this file.
*/
(function () {
  'use strict';
  if (window.__kiwiLaunch) return;
  window.__kiwiLaunch = true;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------ countdown */
  function armClock(root) {
    var target = Date.parse(root.getAttribute('data-target') || '');
    var dials = root.querySelector('[data-ln-dials]');
    var soon = root.querySelector('[data-ln-soon]');
    var units = {};
    [].slice.call(root.querySelectorAll('[data-ln-unit]')).forEach(function (el) {
      units[el.getAttribute('data-ln-unit')] = el;
    });

    /* No usable target — say so once and stop, rather than tick zeros. */
    function giveUp() {
      if (dials) dials.hidden = true;
      if (soon) soon.hidden = false;
    }
    if (!target || isNaN(target)) { giveUp(); return; }

    /* seconds are decoration for assistive tech; the label states the date */
    if (dials) dials.setAttribute('aria-hidden', 'true');

    var timer = null;
    function pad(n) { return n < 10 ? '0' + n : String(n); }

    function tick() {
      var left = target - Date.now();
      if (left <= 0) {
        if (timer) clearInterval(timer);
        giveUp();
        return;
      }
      var secs = Math.floor(left / 1000);
      var d = Math.floor(secs / 86400);
      var h = Math.floor((secs % 86400) / 3600);
      var m = Math.floor((secs % 3600) / 60);
      var s = secs % 60;
      if (units.days) units.days.textContent = String(d);
      if (units.hours) units.hours.textContent = pad(h);
      if (units.mins) units.mins.textContent = pad(m);
      if (units.secs) units.secs.textContent = pad(s);
    }

    tick();
    timer = setInterval(tick, 1000);
  }

  [].slice.call(document.querySelectorAll('[data-ln-clock]')).forEach(armClock);

  /* ------------------------------------------------------ media slider
     The rail scrolls on its own — CSS scroll-snap, swipe, and a focusable
     track for the keyboard. All this adds is the two arrows, and it HIDES
     them when the rail does not overflow: a control that controls nothing
     must not be offered. */
  (function () {
    var root = document.querySelector('[data-ln-rail]');
    if (!root) return;
    var track = root.querySelector('[data-ln-track]');
    var nav = root.querySelector('[data-ln-nav]');
    var prev = root.querySelector('[data-ln-prev]');
    var next = root.querySelector('[data-ln-next]');
    if (!track || !nav || !prev || !next) return;

    function step() {
      var slide = track.querySelector('.ln-slide');
      return slide ? slide.getBoundingClientRect().width + 20 : track.clientWidth * 0.8;
    }
    function sync() {
      var over = track.scrollWidth - track.clientWidth > 4;
      nav.hidden = !over;
      if (!over) return;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
    }
    prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: reduce ? 'auto' : 'smooth' }); });
    next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: reduce ? 'auto' : 'smooth' }); });
    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  })();

  /* --------------------------------------------------------- the RC card
     Holo tilt, eased with GSAP (Rene 2026-10-09: "softer, with easing").
     Two planes: the CARD tilts toward the pointer through quickTo tweens
     (power3.out), and the IEM ([data-ln-holoart], a sibling of the card, not
     a child) turns through the same angles on its own slower tweens (1.8s vs
     0.9s) with a little parallax drift, plus a slow idle bob — so the product reads as floating
     in front of the card rather than printed on it. The foil and glare still
     track the RAW pointer: light is instant, mass is not.

     One gsap.ticker callback per card, parked offscreen and when the tab is
     hidden. Everything written is a transform or a custom property, so the
     card's text never repaints. Reduced motion, no GSAP, or no JS all leave
     the resting pose the CSS declares. */
  function armHolo(wrap) {
    var card = wrap.querySelector('[data-ln-holocard]');
    var shadow = wrap.querySelector('[data-ln-holoshadow]');
    var art = wrap.querySelector('[data-ln-holoart]');
    var floater = art && art.querySelector('[data-ln-holofloat]');
    var gsap = window.gsap;
    if (!card) return;

    var MAX_X = 6, MAX_Y = 8;            /* softer envelope (was 11 / 15) */
    var REST = { rx: -4, ry: 6 };
    var light = { px: 0.62, py: 0.3, hovering: false };
    var autoTilt = window.matchMedia('(hover: none), (max-width: 1023px)');

    function clamp(n, lo, hi) { return Math.min(hi, Math.max(lo, n)); }

    function paintLight(rx, ry) {
      card.style.setProperty('--lnx', light.px.toFixed(4));
      card.style.setProperty('--lny', light.py.toFixed(4));
      /* hue swings with the lean AND across the width, so the sheet changes
         colour as the light travels rather than merely sliding */
      card.style.setProperty('--lnhue', (ry * 4 + (light.px - 0.5) * 40).toFixed(2));
      var lean = (Math.abs(rx) + Math.abs(ry)) / (MAX_X + MAX_Y);
      card.style.setProperty('--lnglare', (0.22 + lean * 0.4).toFixed(3));
      if (shadow) {
        shadow.style.transform = 'translate3d(' + (-ry * 1.8).toFixed(2) + 'px,' + (rx * 1.2).toFixed(2) + 'px,0) scale(' + (1 + lean * 0.08).toFixed(3) + ')';
        shadow.style.opacity = (0.75 + lean * 0.25).toFixed(3);
      }
    }

    if (reduce || !gsap) { paintLight(REST.rx, REST.ry); return; }

    /* rotation:0 — GSAP decomposes the CSS resting matrix with a stray 2D spin */
    gsap.set(card, { rotation: 0, rotationX: REST.rx, rotationY: REST.ry });
    if (art) gsap.set(art, { rotation: 0, z: 60, rotationX: REST.rx, rotationY: REST.ry });

    var cardRX = gsap.quickTo(card, 'rotationX', { duration: 0.9, ease: 'power3.out' });
    var cardRY = gsap.quickTo(card, 'rotationY', { duration: 0.9, ease: 'power3.out' });
    var artRX, artRY, artX, artY;
    if (art) {
      /* slower and lighter than the card: the separate plane */
      artRX = gsap.quickTo(art, 'rotationX', { duration: 1.8, ease: 'power3.out' });
      artRY = gsap.quickTo(art, 'rotationY', { duration: 1.8, ease: 'power3.out' });
      artX = gsap.quickTo(art, 'xPercent', { duration: 1.4, ease: 'power3.out' });
      artY = gsap.quickTo(art, 'yPercent', { duration: 1.4, ease: 'power3.out' });
    }
    if (floater) gsap.to(floater, { yPercent: -3, duration: 2.6, ease: 'sine.inOut', yoyo: true, repeat: -1 });

    function aim(nx, ny) {               /* nx, ny in -1..1 */
      cardRX(-ny * MAX_X);
      cardRY(nx * MAX_Y);
      if (art) {
        artRX(-ny * MAX_X);
        artRY(nx * MAX_Y);
        artX(nx * 3);
        artY(ny * 3);
      }
    }

    var t0 = 0, running = false;
    function tick(time, dt) {
      if (!light.hovering) {
        t0 += dt / 1000;
        if (autoTilt.matches) {
          /* touch screens and narrow viewports have no hover, so the card
             plays the hover itself: a slow loop through the full tilt range
             with the light travelling along, same as a pointer would drive */
          var ax = Math.sin(t0 * 0.7) * 0.9, ay = Math.sin(t0 * 0.45 + 1) * 0.75;
          aim(ax, ay);
          light.px = 0.5 + ax * 0.45;
          light.py = 0.5 + ay * 0.45;
        } else {
          /* ambient sway, and the light drifts home */
          aim(Math.sin(t0 * 0.5) * 0.4, -Math.cos(t0 * 0.37) * 0.35);
          var rate = Math.min(1, dt / 500);
          light.px += (0.5 - light.px) * rate;
          light.py += (0.5 - light.py) * rate;
        }
      }
      paintLight(gsap.getProperty(card, 'rotationX'), gsap.getProperty(card, 'rotationY'));
    }
    function start() { if (!running) { running = true; gsap.ticker.add(tick); } }
    function stop() { if (running) { running = false; gsap.ticker.remove(tick); } }

    /* pause offscreen and when the tab is hidden */
    if (window.IntersectionObserver) {
      new IntersectionObserver(function (es) {
        if (es.some(function (e) { return e.isIntersecting; })) start(); else stop();
      }, { threshold: 0.05 }).observe(wrap);
    }
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') stop(); else start();
    });

    function track(e) {
      var r = card.getBoundingClientRect();
      if (!r.width || !r.height) return;
      light.px = clamp((e.clientX - r.left) / r.width, 0, 1);
      light.py = clamp((e.clientY - r.top) / r.height, 0, 1);
      light.hovering = true;
      aim((light.px - 0.5) * 2, (light.py - 0.5) * 2);
      start();
    }
    function release() { light.hovering = false; }

    wrap.addEventListener('pointermove', track);
    wrap.addEventListener('pointerdown', track);
    wrap.addEventListener('pointerleave', release);
    wrap.addEventListener('pointercancel', release);
    wrap.addEventListener('pointerup', function (e) { if (e.pointerType !== 'mouse') release(); });

    start();
  }

  /* gsap.min.js is deferred from the END of layout/theme.liquid, so it runs
     after this file (readyState is already 'interactive' here). Every
     deferred script has run by DOMContentLoaded, so wait for that unless
     GSAP is already in or the page has finished loading. */
  function armHolos() { [].slice.call(document.querySelectorAll('[data-ln-holo]')).forEach(armHolo); }
  if (window.gsap || document.readyState === 'complete') armHolos();
  else document.addEventListener('DOMContentLoaded', armHolos, { once: true });

  /* ------------------------------------------- card product cut-out
     The voucher card floats the product over the star, which needs a
     transparent PNG. Most catalog shots are on white, so when a shot's corner
     is white we flood-fill the background away from the edges (interior
     whites — shells, cables — are untouched because the fill only reaches
     what is connected to the border), feathering the fringe. A real cut-out
     (transparent corner) is left alone, and a canvas that cannot be read
     leaves the photo as it was. */
  function keyOutWhite(img) {
    try {
      var w = img.naturalWidth, h = img.naturalHeight;
      if (!w || !h) return;
      var c = document.createElement('canvas');
      c.width = w; c.height = h;
      var x = c.getContext('2d');
      x.drawImage(img, 0, 0);
      var id = x.getImageData(0, 0, w, h), d = id.data;
      if (d[3] < 250) return;                       /* already a cut-out */
      var HARD = 238, SOFT = 212;                   /* min channel thresholds */
      function lum(i) { return Math.min(d[i], d[i + 1], d[i + 2]); }
      if (lum(0) < HARD) return;                    /* not on white */
      var seen = new Uint8Array(w * h), stack = [];
      for (var i = 0; i < w; i++) { stack.push(i, (h - 1) * w + i); }
      for (var j = 0; j < h; j++) { stack.push(j * w, j * w + w - 1); }
      while (stack.length) {
        var p = stack.pop();
        if (seen[p]) continue;
        seen[p] = 1;
        var k = p * 4, v = lum(k);
        if (v < SOFT) continue;
        d[k + 3] = v >= HARD ? 0 : Math.round(255 * (HARD - v) / (HARD - SOFT));
        if (v < HARD) continue;                     /* fringe: fade, stop */
        var px = p % w;
        if (px > 0) stack.push(p - 1);
        if (px < w - 1) stack.push(p + 1);
        if (p >= w) stack.push(p - w);
        if (p < w * (h - 1)) stack.push(p + w);
      }
      x.putImageData(id, 0, 0);
      c.toBlob(function (b) {
        if (!b) return;
        img.removeAttribute('srcset');
        img.src = URL.createObjectURL(b);
      }, 'image/png');
    } catch (e) { /* tainted canvas: keep the photo */ }
  }
  [].slice.call(document.querySelectorAll('[data-ln-cutout]')).forEach(function (img) {
    if (img.complete && img.naturalWidth) keyOutWhite(img);
    else img.addEventListener('load', function once() { img.removeEventListener('load', once); keyOutWhite(img); });
  });

  /* ---------------------------------------------- reservation add-to-cart
     The markup is a REAL form posting to /cart/add with return_to=/checkout,
     so with no JS the reservation still goes through. Here we intercept it and
     add over fetch instead, which keeps the buyer on the page and opens the
     shared drawer. */
  var form = document.querySelector('[data-ln-form]');
  var btn = document.querySelector('[data-ln-add]');
  if (!btn || !form) return;

  var label = btn.querySelector('[data-ln-addlabel]') || btn;
  var msg = form.querySelector('[data-ln-msg]');

  form.addEventListener('submit', function (ev) {
    var vid = +btn.getAttribute('data-vid');
    if (!vid || btn.disabled) return;      /* let the browser do its thing */
    ev.preventDefault();

    btn.disabled = true;
    label.textContent = btn.getAttribute('data-busy') || 'Working…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: vid, quantity: 1 })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return fetch('/cart.js').then(function (x) { return x.json(); });
      })
      .then(function (c) {
        var n = document.getElementById('cartn');
        if (n && c && c.item_count != null) n.textContent = c.item_count;
        label.textContent = btn.getAttribute('data-done') || 'Added to cart';
        if (window.gsap && !reduce) {
          window.gsap.fromTo(btn, { scale: 0.94 }, { scale: 1, duration: 0.45, ease: 'back.out(2.4)' });
        }
        document.dispatchEvent(new CustomEvent('kiwi:cart-open'));
      })
      .catch(function () {
        /* Say it failed rather than sitting on "Reserving…" forever — the
           customer needs to know their dollar did NOT go through, and the
           form is still there to submit the normal way. */
        label.textContent = btn.getAttribute('data-label') || 'Reserve now';
        btn.setAttribute('data-failed', '1');
        if (msg) msg.textContent = 'That did not go through. Try again.';
      })
      .finally(function () {
        setTimeout(function () {
          btn.disabled = false;
          label.textContent = btn.getAttribute('data-label') || 'Reserve now';
          btn.removeAttribute('data-failed');
          if (msg) msg.textContent = '';
        }, 2200);
      });
  });
})();
